import { useState, useEffect } from "react";
import { Upload, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/lib/logger';
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useImpersonation } from "@/contexts/ImpersonationContext";

interface NorthDesignSettingsProps {
  northId: string;
  customAgentName?: string | null;
  customAgentHeadshotUrl?: string | null;
  customLogoUrl?: string | null;
  primaryBrandColor?: string | null;
  secondaryBrandColor?: string | null;
  defaultPrimaryColor?: string;
  defaultSecondaryColor?: string;
  defaultLogoUrl?: string | null;
  sellerNames?: string | null;
  isPublished?: boolean;
  useCustomBranding?: boolean;
  onUpdate: () => void;
}

export const NorthDesignSettings = ({
  northId,
  customAgentName,
  customAgentHeadshotUrl,
  customLogoUrl,
  primaryBrandColor,
  secondaryBrandColor,
  defaultPrimaryColor = "#0c0f24",
  defaultSecondaryColor = "#1b489b",
  defaultLogoUrl,
  sellerNames,
  isPublished = false,
  useCustomBranding = false,
  onUpdate,
}: NorthDesignSettingsProps) => {
  const { toast } = useToast();
  const { getEffectiveUserId } = useImpersonation();
  const { agents } = useTeamAgents();
  const [canSelectFromRoster, setCanSelectFromRoster] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [customBrandingEnabled, setCustomBrandingEnabled] = useState(useCustomBranding);
  const [agentName, setAgentName] = useState(customAgentName || "");
  const [headshotUrl, setHeadshotUrl] = useState(customAgentHeadshotUrl || "");
  const [logoUrl, setLogoUrl] = useState(customLogoUrl || "");
  const [primaryColor, setPrimaryColor] = useState(primaryBrandColor || defaultPrimaryColor);
  const [secondaryColor, setSecondaryColor] = useState(secondaryBrandColor || defaultSecondaryColor);
  const [sellerNamesValue, setSellerNamesValue] = useState(sellerNames || "");
  const [isSaving, setIsSaving] = useState(false);

  // Check admin status and match current agent to roster
  useEffect(() => {
    const checkAdminAndMatchAgent = async () => {
      try {
        const effectiveUserId = await getEffectiveUserId();
        if (!effectiveUserId) return;

        // Check if user is admin or agent (both can select from roster)
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', effectiveUserId)
          .in('role', ['admin', 'agent'])
          .maybeSingle();

        setCanSelectFromRoster(!!roleData);

        // If user has roster access and we have a current agent name, try to match it to roster
        if (roleData && customAgentName && agents.length > 0) {
          const matchedAgent = agents.find(
            a => a.name.toLowerCase().trim() === customAgentName.toLowerCase().trim()
          );
          if (matchedAgent) {
            setSelectedAgentId(matchedAgent.id);
            setAgentName(matchedAgent.name);
            setHeadshotUrl(matchedAgent.headshot_url || "");
          }
        }
      } catch (error) {
        logger.error('Error checking admin status or matching agent:', error);
      }
    };

    checkAdminAndMatchAgent();
  }, [customAgentName, agents, getEffectiveUserId]);

  // Handle agent selection from dropdown
  useEffect(() => {
    if (canSelectFromRoster) {
      if (selectedAgentId && selectedAgentId !== "none") {
        const selectedAgent = agents.find(a => a.id === selectedAgentId);
        if (selectedAgent) {
          setAgentName(selectedAgent.name);
          setHeadshotUrl(selectedAgent.headshot_url || "");
        }
      } else if (selectedAgentId === "none") {
        // Clear selection
        setAgentName("");
        setHeadshotUrl("");
      }
    }
  }, [selectedAgentId, agents, canSelectFromRoster]);

  // Display values - either custom or default based on toggle
  const displayLogoUrl = customBrandingEnabled ? logoUrl : (defaultLogoUrl || logoUrl);
  const displayPrimaryColor = customBrandingEnabled ? primaryColor : defaultPrimaryColor;
  const displaySecondaryColor = customBrandingEnabled ? secondaryColor : defaultSecondaryColor;

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "headshot" | "logo"
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `${type}-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("logos").getPublicUrl(filePath);

      if (type === "headshot") {
        setHeadshotUrl(publicUrl);
      } else {
        setLogoUrl(publicUrl);
      }

      toast({
        title: "Success",
        description: `${type === "headshot" ? "Headshot" : "Logo"} uploaded successfully`,
      });
    } catch (error) {
      logger.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to upload file",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("norths")
        .update({
          custom_agent_name: agentName || null,
          custom_agent_headshot_url: headshotUrl || null,
          custom_logo_url: customBrandingEnabled ? logoUrl || null : null,
          primary_brand_color: customBrandingEnabled ? primaryColor : null,
          secondary_brand_color: customBrandingEnabled ? secondaryColor : null,
          seller_names: sellerNamesValue || null,
          use_custom_branding: customBrandingEnabled,
        })
        .eq("id", northId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Design settings saved successfully",
      });

      onUpdate();
    } catch (error) {
      logger.error("Error saving design settings:", error);
      toast({
        title: "Error",
        description: "Failed to save design settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold mb-6">Branding</h2>

        <div className="space-y-4">
          <div>
            <Label htmlFor="agent-name">
              Listing Agent <span className="text-destructive">*</span>
            </Label>
            {canSelectFromRoster ? (
              <Select 
                value={selectedAgentId || undefined} 
                onValueChange={(value) => setSelectedAgentId(value === "none" ? "" : value)}
                disabled={isPublished}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder={agents.length > 0 ? "Select an agent from roster" : "No agents in roster yet"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Clear selection)</SelectItem>
                  {agents.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No agents in roster. Add agents in Settings → Team Roster
                    </div>
                  ) : (
                    agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={agent.headshot_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {agent.name[0]?.toUpperCase() || "A"}
                            </AvatarFallback>
                          </Avatar>
                          <span>{agent.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="agent-name"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Enter your name"
                className="mt-2"
                disabled={isPublished}
              />
            )}
          </div>

          <div>
            <Label htmlFor="seller-names">
              Seller Name(s)
            </Label>
            <Input
              id="seller-names"
              value={sellerNamesValue}
              onChange={(e) => setSellerNamesValue(e.target.value)}
              placeholder="e.g., John & Jane Smith"
              className="mt-2"
              disabled={isPublished}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold mb-4">Headshot</h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border-2 border-border bg-muted flex items-center justify-center overflow-hidden">
              {headshotUrl ? (
                <img
                  src={headshotUrl}
                  alt="Agent headshot"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              {canSelectFromRoster && selectedAgentId && selectedAgentId !== "none" ? (
                <p className="text-sm text-muted-foreground">
                  Headshot from roster
                </p>
              ) : (
                <>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "headshot")}
                    className="hidden"
                    id="headshot-upload"
                    disabled={isPublished}
                  />
                  <Label htmlFor="headshot-upload">
                    <Button variant="outline" size="sm" asChild disabled={isPublished}>
                      <span className="cursor-pointer">
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                      </span>
                    </Button>
                  </Label>
                </>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">
            Logo {!customBrandingEnabled && <span className="text-xs font-normal text-muted-foreground">(from team settings)</span>}
          </h3>
          <div className="flex items-center gap-4">
            <div className={`w-52 h-20 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden ${!customBrandingEnabled ? 'opacity-50' : ''}`}>
              {displayLogoUrl ? (
                <img
                  src={displayLogoUrl}
                  alt="Company logo"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <Upload className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, "logo")}
                className="hidden"
                id="logo-upload"
                disabled={isPublished || !customBrandingEnabled}
              />
              <Label htmlFor="logo-upload">
                <Button variant="outline" size="sm" asChild disabled={isPublished || !customBrandingEnabled}>
                  <span className={customBrandingEnabled && !isPublished ? "cursor-pointer" : "cursor-not-allowed"}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </Label>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Branding & Colors</h3>
            <p className="text-sm text-muted-foreground mt-1">
              By default, reports use your team branding from Settings. Enable custom branding to override for this report.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="custom-branding" className="text-sm font-medium">
              Use custom branding
            </Label>
            <Switch
              id="custom-branding"
              checked={customBrandingEnabled}
              onCheckedChange={setCustomBrandingEnabled}
              disabled={isPublished}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div>
            <Label htmlFor="primary-color" className="mb-2 block">
              Primary Brand Color {!customBrandingEnabled && <span className="text-xs text-muted-foreground">(from team settings)</span>}
            </Label>
            <div className="flex items-center gap-4">
              <div
                className="w-80 h-32 rounded-lg border border-border"
                style={{ backgroundColor: displayPrimaryColor }}
              />
              <div className={`flex items-center gap-2 bg-background border border-border rounded px-3 py-1.5 ${!customBrandingEnabled ? 'opacity-50' : ''}`}>
                <input
                  type="color"
                  id="primary-color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-0 h-0 invisible"
                  disabled={isPublished || !customBrandingEnabled}
                />
                <Label htmlFor="primary-color" className={`font-mono text-sm ${customBrandingEnabled && !isPublished ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                  {displayPrimaryColor.toUpperCase()}
                </Label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="secondary-color" className="mb-2 block">
              Secondary Brand Color {!customBrandingEnabled && <span className="text-xs text-muted-foreground">(from team settings)</span>}
            </Label>
            <div className="flex items-center gap-4">
              <div
                className="w-80 h-32 rounded-lg border border-border"
                style={{ backgroundColor: displaySecondaryColor }}
              />
              <div className={`flex items-center gap-2 bg-background border border-border rounded px-3 py-1.5 ${!customBrandingEnabled ? 'opacity-50' : ''}`}>
                <input
                  type="color"
                  id="secondary-color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-0 h-0 invisible"
                  disabled={isPublished || !customBrandingEnabled}
                />
                <Label htmlFor="secondary-color" className={`font-mono text-sm ${customBrandingEnabled && !isPublished ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                  {displaySecondaryColor.toUpperCase()}
                </Label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <Button onClick={handleSave} disabled={isSaving || isPublished} className="bg-black text-white hover:bg-black/90">
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};
